import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo,
} from 'react';
import PropTypes from 'prop-types';

// Utils
import { useMutation } from 'react-query';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import adminAccountSchema from '../../../../validations/administration/adminAccountSchema';

// Components
import InputField from '../../../atoms/InputField/InputField';
import SelectField from '../../../atoms/SelectField/SelectField';

// Services
import useAdministratorsService from '../../../../services/administratorsService';
import useModal from '../../../../utils/hooks/useModal';
import FlashMessage from '../../../molecules/FlashMessage/FlashMessage';
import useNotification from '../../../../utils/hooks/useNotification';
import { useAppContext } from '../../../../store/AppContext';

const AccountEdition = forwardRef(({
  tableAdminType, account, roles, setAdministrators,
}, ref) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const { displayNotification } = useNotification();
  const [, stateDispatch] = useAppContext();

  const { putAdminAccount } = useAdministratorsService();

  const rolesOptions = useMemo(() => (roles?.map((role) => (
    { label: role.name, value: role.id, isSuperAdmin: role.isSuperAdmin }
  )) || []), [roles]);

  const { isLoading: putAccountLoading, mutate: putAccountMutate, error } = useMutation(
    ({ accountId, values }) => putAdminAccount({ type: tableAdminType, accountId, values }),
    {
      onSuccess: (res) => {
        closeModal();
        if (res?.data) {
          setAdministrators((state) => state.map((admin) => (admin.id === res.data.id ? res.data : admin)));
        }

        displayNotification({
          message: t('admin.edition.success', { name: `${res.data.firstName} ${res.data.lastName}` }),
        });
      },
    },
  );

  useEffect(() => {
    stateDispatch({ type: 'SET_MODAL_QUERY_LOADING', payload: putAccountLoading ? 'putAccount' : null });
  }, [putAccountLoading]);

  const formik = useFormik({
    initialValues: {
      firstname: account?.firstName || '',
      lastname: account?.lastName || '',
      email: account?.emailAddress || '',
      role: rolesOptions?.find((option) => option.value === account.roleId) || null,
    },
    onSubmit: (values) => {
      putAccountMutate({
        accountId: account.id,
        values: {
          emailAddress: values.email,
          firstName: values.firstname,
          lastName: values.lastname,
          roleId: values.role?.value,
        },
      });
    },
    validationSchema: adminAccountSchema,
  });

  useImperativeHandle(
    ref,
    () => ({
      submitForm: () => {
        formik.handleSubmit();
      },
    }),
  );

  const getInput = useCallback((name) => (
    <div className="m-b-15">
      <InputField
        id={`admin_edition_${name}`}
        name={name}
        value={formik.values[name]}
        label={t(`admin.edition.form.${name}`)}
        formik={{
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
        }}
        error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        isRequired
      />
    </div>
  ), [formik]);

  return (
    <div>
      <form>
        {getInput('firstname')}
        {getInput('lastname')}
        {getInput('email')}

        <SelectField
          id="admin_edition_roles"
          name="role"
          options={rolesOptions}
          label={t('admin.edition.form.role')}
          error={formik.errors?.role && formik.touched?.role ? formik.errors.role : null}
          setFieldTouched={formik.setFieldTouched}
          setFieldValue={formik.setFieldValue}
          value={formik.values.role}
          isRequired
        />

        {error ? (
          <div className="m-t-20">
            <FlashMessage
              color="error"
              message={t(`code_errors.${error?.response?.data?.code || 'generic'}`)}
            />
          </div>
        ) : null}
      </form>
    </div>
  );
});

AccountEdition.propTypes = {
  tableAdminType: PropTypes.string.isRequired,
  account: PropTypes.shape({
    emailAddress: PropTypes.string,
    firstName: PropTypes.string,
    id: PropTypes.string,
    isActive: PropTypes.bool,
    lastName: PropTypes.string,
    roleId: PropTypes.string,
  }).isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape({})),
  setAdministrators: PropTypes.func,
};

AccountEdition.defaultProps = {
  roles: [],
  setAdministrators: () => {},
};

export default AccountEdition;
